<template>
    <div
        class="alert-card h-full gap-3 flex flex-col w-full pr-3 overflow-visible"
        @mouseover="showOptions = true"
        @mouseleave="handleOnMouseLeave"
    >
        <div class="flex flex-col gap-3 relative w-full">
            <div
                class="w-full relative bg-white dark:bg-slate-800 rounded-lg flex border border-slate-200 dark:border-slate-700 shadow dark:shadow-slate-900/50 cursor-pointer"
                :class="{
                    'opacity-50':
                        alert.status === 'completed' ||
                        alert.status === 'unavailable' ||
                        alert.status === 'expired',
                    'h-36': !widgetView,
                    'items-end p-3': !includeImage,
                }"
                @click="() => handleClick(alert)"
            >
                <ImageLoader v-if="includeImage" class="w-full h-36">
                    <template #image>
                        <img
                            class="rounded-lg h-full w-full object-cover"
                            :src="alert.property.image_location"
                        />
                    </template>
                    <template #preloader>
                        <div
                            class="w-full h-full rounded-lg bg-slate-200 shimmer"
                        ></div>
                    </template>
                    <template #error>
                        <div
                            class="w-full h-full rounded-lg object-fill flex items-center justify-center text-center flex-col bg-slate-200 text-slate-400"
                        >
                            <Icon icon="no-image" />
                        </div>
                    </template>
                </ImageLoader>
                <div
                    v-else
                    class="pt-10 flex-col justify-start items-start gap-1 flex text-slate-800 dark:text-slate-100 text-sm font-medium"
                >
                    <AlertDates :alert="alert" :widgetView="widgetView" />
                    <div class="justify-start items-center gap-2 flex">
                        <div class="text-slate-500 dark:text-slate-400 text-sm font-normal">
                            {{ $filters.ucWords(alert.alert_type) }}
                        </div>
                        <div
                            v-show="alert.max_points"
                            class="w-0.5 h-0.5 bg-slate-500 rounded-full"
                        ></div>
                        <div
                            v-if="alert.max_points"
                            class="text-slate-500 dark:text-slate-400 text-sm font-normal"
                        >
                            {{ $filters.formatThousand(alert.max_points) }}
                        </div>
                    </div>
                </div>
                <!-- START STATUS PILL -->
                <div class="absolute top-2 left-2">
                    <AlertStatusBadge :alert="alert" />
                </div>
                <!-- START BRAND IMAGE -->
                <div
                    class="h-9 p-2 bg-white/90 rounded justify-center items-center inline-flex absolute bottom-2 right-2"
                    v-show="!widgetView"
                >
                    <img
                        :src="alert.property.program?.image"
                        class="max-w-[68px] h-auto max-h-7"
                    />
                </div>
                <!-- END BRAND IMAGE -->
            </div>
            <!-- START OPTIONS BUTTON -->
            <div class="absolute gap-1 top-2.5 right-3" v-show="showOptions">
                <WidgetsOptionsButton
                    :alert="alert"
                    :isOpen="menuOpen"
                    :shape="computedButtonShape"
                    @toggle-menu="toggleMenu"
                />
            </div>
            <!-- END OPTIONS BUTTON -->
            <!-- START PROPERTY NAME AND ALERT DATES -->
            <span
                v-if="includeImage"
                @click="() => handleClick(alert)"
                class="flex-col justify-start items-start gap-2 flex w-full cursor-pointer"
            >
                <div
                    class="truncate text-black/95 dark:text-slate-100 text-sm font-medium leading-tight whitespace-nowrap"
                    style="max-width: 190px"
                    v-text="alert.property.name"
                ></div>
                <div class="items-center gap-2 flex text-slate-500 dark:text-slate-400">
                    <Icon icon="calendar" svgClass="w-5 h-5" pathClass="fill-slate-500" />
                    <AlertDates :alert="alert" :widgetView="widgetView" />
                </div>
            </span>
        </div>
    </div>
</template>
<script>
import { ref, computed } from 'vue';
import { useAlertsStore } from '@/store/alerts.js';
import AlertDates from '@/components/alerts/alert-dates.vue';
import AlertStatusBadge from '@/components/alerts/alert-status-badge/alert-status-badge.vue';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import WidgetsOptionsButton from '@/components/alerts/widgets-options-button/widgets-options-button.vue';

export default {
    name: 'AlertItem',
    components: {
        AlertDates,
        AlertStatusBadge,
        Icon,
        ImageLoader,
        WidgetsOptionsButton,
    },
    props: {
        alert: {
            type: Object,
            required: true,
        },
        includeImage: {
            type: Boolean,
            required: false,
            default: true,
        },
        widgetView: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    setup(props) {
        const showOptions = ref(false);
        const menuOpen = ref(false);
        const alertsStore = useAlertsStore();
        const { setShowAlertModal, setAlertModalState, selectAlert } =
            alertsStore;

        const handleClick = (a) => {
            setAlertModalState('step2.view');
            selectAlert(a);
            setShowAlertModal(true);
        };
        const computedButtonShape = computed(() => {
            if (props.includeImage) {
                return 'circle';
            } else {
                return 'square';
            }
        });

        const handleOnMouseLeave = () => {
            menuOpen.value = false;
            showOptions.value = false;
        };

        const toggleMenu = () => {
            menuOpen.value = !menuOpen.value;
        };

        return {
            computedButtonShape,
            handleClick,
            handleOnMouseLeave,
            menuOpen,
            setShowAlertModal,
            showOptions,
            toggleMenu,
        };
    },
};
</script>

<style scoped>
.alert-card {
    position: relative;
}

.alert-card::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.2);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
}

.alert-card:hover::after {
    opacity: 1;
}
</style>
